import { Box, Button, Dialog, DialogContent, TextField, Typography } from "@mui/material";
import moment, { Moment } from "moment";
import React, { FC, useEffect, useState } from "react";
import { ICustomTimeRangeDetails } from ".";
import {
	DateRange,
	StaticDateRangePicker,
	TimePicker,
	dateRangeCalendarClasses,
	pickersLayoutClasses,
	renderTimeViewClock
} from "@mui/x-date-pickers-pro";
import { TIME_RANGE_OPTIONS_E } from "../../types";

interface ICustomRangeSelectionDialogProps {
	open: boolean;
	defaultValues?: ICustomTimeRangeDetails;
	showPredefinedValues?: boolean;
	defaultPredefinedValue?: TIME_RANGE_OPTIONS_E;
	onClose: () => void;
	onSubmit: (updatedValue: ICustomTimeRangeDetails, timeRange: TIME_RANGE_OPTIONS_E) => void;
}

interface IPredefinedFilterDetails {
	key: TIME_RANGE_OPTIONS_E;
	name: string;
}

const PREDEFINED_FILTERS_LIST: IPredefinedFilterDetails[] = [
	{ key: TIME_RANGE_OPTIONS_E.TODAY, name: "Live / Today" },
	{ key: TIME_RANGE_OPTIONS_E.YESTERDAY, name: "Yesterday" },
	{ key: TIME_RANGE_OPTIONS_E.WEEK, name: "Last 7 Days" },
	{ key: TIME_RANGE_OPTIONS_E.MONTH, name: "Last Month" },
	{ key: TIME_RANGE_OPTIONS_E.QUARTER, name: "Last 3 Months" },
	{ key: TIME_RANGE_OPTIONS_E.HALF_YEAR, name: "Last 6 Months" }
];

const CustomRangeSelectionDialog: FC<ICustomRangeSelectionDialogProps> = (props) => {
	const { open, defaultValues, showPredefinedValues, defaultPredefinedValue, onClose, onSubmit } = props;

	const [predefinedRange, setPredefinedRange] = useState<TIME_RANGE_OPTIONS_E>(TIME_RANGE_OPTIONS_E.TODAY);
	const [dateRange, setDateRange] = useState<ICustomTimeRangeDetails>({
		from: moment().startOf("day"),
		to: moment().endOf("day")
	});
	const [timeRange, setTimeRange] = useState<ICustomTimeRangeDetails>({
		from: moment().startOf("day"),
		to: moment().endOf("day")
	});

	function handleChangeDateRangeValues(updatedValue: DateRange<moment.Moment>) {
		if (updatedValue[0] && updatedValue[1]) {
			setDateRange({
				from: updatedValue[0].startOf("day"),
				to: updatedValue[1].endOf("day")
			});
			setPredefinedRange(TIME_RANGE_OPTIONS_E.CUSTOM);
		}
	}

	function handleChangeTimeRangeValues(value: Moment, key: "from" | "to") {
		setTimeRange((prev) => ({
			...prev,
			[key]: value
		}));
		setPredefinedRange(TIME_RANGE_OPTIONS_E.CUSTOM);
	}

	function handleApplyButtonClick() {
		const updatedRange = { ...dateRange };

		updatedRange.from = updatedRange.from
			.set("hours", timeRange.from.get("hours"))
			.set("minutes", timeRange.from.get("minutes"));

		updatedRange.to = updatedRange.to
			.set("hours", timeRange.to.get("hours"))
			.set("minutes", timeRange.to.get("minutes"));

		onSubmit(updatedRange, predefinedRange);
	}

	function handlePredefinedDateValueSelection(updatedValue: TIME_RANGE_OPTIONS_E) {
		switch (updatedValue) {
			case TIME_RANGE_OPTIONS_E.TODAY:
				setDateRange({
					from: moment().startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.YESTERDAY:
				setDateRange({
					from: moment().subtract(1, "day").startOf("day"),
					to: moment().subtract(1, "day").endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.WEEK:
				setDateRange({
					from: moment().subtract(1, "week").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.MONTH:
				setDateRange({
					from: moment().subtract(1, "month").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.QUARTER:
				setDateRange({
					from: moment().subtract(3, "months").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			case TIME_RANGE_OPTIONS_E.HALF_YEAR:
				setDateRange({
					from: moment().subtract(6, "months").startOf("day"),
					to: moment().endOf("day")
				});
				break;

			default:
				break;
		}

		if (updatedValue !== TIME_RANGE_OPTIONS_E.CUSTOM) {
			setTimeRange({
				from: moment().startOf("day"),
				to: moment().endOf("day")
			});
		}

		setPredefinedRange(updatedValue);
	}

	useEffect(() => {
		if (open) {
			if (defaultPredefinedValue) setPredefinedRange(defaultPredefinedValue);
			if (defaultValues) {
				setDateRange(defaultValues);
				setTimeRange(defaultValues);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const startDateString = dateRange.from.format("DD/MM/YYYY");
	const endDateString = dateRange.to.format("DD/MM/YYYY");

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogContent classes={{ root: "dialog-content-root" }}>
				{showPredefinedValues ? (
					<Box className="predefined-date-value-selection-wrapper">
						{PREDEFINED_FILTERS_LIST.map((predefinedFilterItem) => (
							<Box
								key={predefinedFilterItem.key}
								className={`predefined-date-value ${predefinedRange === predefinedFilterItem.key ? "active" : ""}`}
								onClick={() => handlePredefinedDateValueSelection(predefinedFilterItem.key)}
							>
								<Typography>{predefinedFilterItem.name}</Typography>
							</Box>
						))}
					</Box>
				) : null}

				<Typography variant="h6" marginTop="1rem">
					Please select start and end date
				</Typography>

				<Box className="date-range-toolbar-section-wrapper">
					<Box className="date-range-inputs-wrapper">
						<TextField label="From" value={startDateString} size="small" fullWidth autoComplete="off" />
						<TextField label="To" value={endDateString} size="small" fullWidth autoComplete="off" />
					</Box>
				</Box>

				<Typography variant="h6">Please select start and end time</Typography>

				<Box className="date-range-toolbar-section-wrapper">
					<Box className="date-range-inputs-wrapper">
						<TimePicker
							label="From"
							value={timeRange.from}
							onChange={(value) => handleChangeTimeRangeValues(value ?? moment().startOf("day"), "from")}
							closeOnSelect={false}
							viewRenderers={{
								hours: renderTimeViewClock,
								minutes: renderTimeViewClock
							}}
							slotProps={{ textField: { variant: "outlined", size: "small", fullWidth: true } }}
						/>

						<TimePicker
							label="To"
							value={timeRange.to}
							onChange={(value) => handleChangeTimeRangeValues(value ?? moment().endOf("day"), "to")}
							closeOnSelect={false}
							viewRenderers={{
								hours: renderTimeViewClock,
								minutes: renderTimeViewClock
							}}
							slotProps={{ textField: { variant: "outlined", size: "small", fullWidth: true } }}
						/>
					</Box>
				</Box>

				<StaticDateRangePicker
					disableFuture
					disableHighlightToday
					disableAutoMonthSwitching
					calendars={1}
					value={[dateRange.from, dateRange.to]}
					onChange={handleChangeDateRangeValues}
					sx={{
						[`.${pickersLayoutClasses.contentWrapper}`]: {
							alignItems: "center"
						},
						[`.${dateRangeCalendarClasses.monthContainer}`]: {
							border: "1px solid #00000099",
							borderRadius: "4px"
						}
					}}
					slots={{
						toolbar: () => <></>,
						actionBar: () => <></>
					}}
				/>

				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1rem" }}>
					<Button variant="contained" color="primary" sx={{ paddingX: 3 }} onClick={handleApplyButtonClick}>
						Apply
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default CustomRangeSelectionDialog;
